// Talks to the Simplero Coach app shell (simplero-coach-mobile-app/App.js).
// The shell covers the WebView with a loading screen that also lets the user
// start typing; telling it the composer is usable drops that screen as soon as
// possible instead of waiting for every image and font on the page.
export const NATIVE_CHANNEL = 'simplero-coach'

// IndexedDB can hang in WKWebView; never let it block the hand-off for good.
const WAIT_TIMEOUT_MS = 2000

const post = (bridge, message) =>
  bridge.postMessage(JSON.stringify({ channel: NATIVE_CHANNEL, ...message }))

// Drafts already saved here. If the app was killed before our acknowledgement
// reached it, it offers the same id again after relaunch; this keeps that from
// inserting the text a second time.
const SAVED_DRAFTS_KEY = 'simplero-coach-native-drafts'
const savedDraftIds = (win) => {
  try {
    return JSON.parse(win.localStorage.getItem(SAVED_DRAFTS_KEY)) || []
  } catch {
    return []
  }
}
const rememberSavedDraft = (win, id) => {
  try {
    const ids = [...savedDraftIds(win).filter((saved) => saved !== id), id]
    win.localStorage.setItem(SAVED_DRAFTS_KEY, JSON.stringify(ids.slice(-20)))
  } catch {
    // Only costs the relaunch dedupe.
  }
}

// Settles when `promise` does or after `ms`, whichever is first; never
// rejects. Resolves true only when `promise` fulfilled in time.
export const withTimeout = (promise, ms) =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve(false), ms)
    Promise.resolve(promise).then(
      () => {
        clearTimeout(timer)
        resolve(true)
      },
      () => {
        clearTimeout(timer)
        resolve(false)
      }
    )
  })

// Resolves false outside the app, where there is nothing to announce to.
//
// Waits for `restored` (the saved-draft restore) before announcing, so the
// app's text lands after an older saved draft rather than making the restore
// skip it. Past the timeout the restore is skipped, as it is when the user
// types before it finishes. The app may offer the same draft more than once (on ready and on
// load end) and clears its copy once we acknowledge it, so each id is inserted
// once and acknowledged only after `insertDraft`'s returned save settles: until
// then the app's copy is the only durable one. An aborted `signal` (the
// composer went away) stops both, so the app keeps its copy for the next one,
// unless the save already landed: then the draft is kept, and a second copy
// would only duplicate it.
//
// `submit` (the Swift app only, simplero-coach-ios) sends a message the app's
// native composer finished: `{ id, text, attachments }`. It throws when nothing
// was sent, and is acknowledged either way so the app knows whether to keep the
// text. `details` adds fields to the ready message (e.g. the CSRF token the app
// uses for its own requests). `removeAttachment(resourceType, resourceId)` lets the app's ✕
// take off a source this page attached (shown in the app as `page-attachments`).
export async function announceComposerReady({
  win = window,
  restored,
  insertDraft,
  submit,
  removeAttachment,
  details = {},
  signal,
  timeoutMs = WAIT_TIMEOUT_MS,
}) {
  const bridge = win.ReactNativeWebView
  if (!bridge) return false

  await withTimeout(restored, timeoutMs)
  if (signal?.aborted) return false

  const inserted = new Set()
  const native = {
    insertDraft(text, { focus = false, id } = {}) {
      if (signal?.aborted || inserted.has(id)) return
      if (savedDraftIds(win).includes(id)) {
        post(bridge, { type: 'draft-inserted', id })
        return
      }
      // A throw means nothing was inserted: no ack, so the app keeps its copy.
      const saved = insertDraft(text, { focus })
      inserted.add(id)
      // Whenever it lands, even after the acknowledgement gave up waiting.
      Promise.resolve(saved).then(
        () => rememberSavedDraft(win, id),
        () => null
      )
      withTimeout(saved, timeoutMs).then((stored) => {
        if (stored || !signal?.aborted)
          post(bridge, { type: 'draft-inserted', id })
      })
    },
  }
  if (submit) {
    native.submit = (message = {}) => {
      if (signal?.aborted) return
      try {
        submit(message)
        post(bridge, { type: 'submitted', id: message.id })
      } catch (error) {
        post(bridge, {
          type: 'submit-failed',
          id: message.id,
          reason: String(error?.message || error),
        })
      }
    }
  }
  if (removeAttachment) native.removeAttachment = removeAttachment
  win.SimpleroCoachNative = native
  signal?.addEventListener('abort', () => {
    if (win.SimpleroCoachNative === native) delete win.SimpleroCoachNative
  })
  post(bridge, {
    ...details,
    type: 'composer-ready',
    acceptsDraft: true,
    ...(submit ? { acceptsSubmit: true } : {}),
  })
  return true
}

// The edit-a-past-message form, in the Swift app: the app's composer shows `text` and its Send
// calls `submit` (acknowledged like the main composer's). The main composer's bridge comes back
// when `signal` aborts (the form saved or was canceled), and the app hears `edit-ended`. The
// app calls `acceptEdit` when it took over (the page then hides its own form) and `cancelEdit`
// for its ✕.
export function announceEditing({
  win = window,
  text,
  submit,
  accepted,
  cancel,
  signal,
}) {
  const bridge = win.ReactNativeWebView
  if (!bridge || signal?.aborted) return false

  const previous = win.SimpleroCoachNative
  // An app that doesn't know edits (before Coach 90) never accepts, and keeps composing new
  // messages: its sends go to the main composer until the app takes over.
  let isAccepted = false
  const native = {
    ...previous,
    submit(message = {}) {
      if (!isAccepted && previous?.submit) return previous.submit(message)
      if (signal?.aborted) return
      try {
        submit(message)
        post(bridge, { type: 'submitted', id: message.id })
      } catch (error) {
        post(bridge, {
          type: 'submit-failed',
          id: message.id,
          reason: String(error?.message || error),
        })
      }
    },
    acceptEdit: () => {
      isAccepted = true
      accepted?.()
    },
    cancelEdit: () => cancel?.(),
  }
  win.SimpleroCoachNative = native
  signal?.addEventListener('abort', () => {
    if (win.SimpleroCoachNative === native) win.SimpleroCoachNative = previous
    post(bridge, { type: 'edit-ended' })
  })
  post(bridge, { type: 'edit-started', text })
  return true
}

// Editor HTML to native composer text: paragraphs become lines, and a blockquote's lines start
// with "> " (nativeTextToHtml reads them back as a blockquote).
export function htmlToNativeText(html, doc = globalThis.document) {
  const root = doc.createElement('div')
  root.innerHTML = String(html || '')
  // A hard break (Shift-Enter) is a line break in the text.
  root.querySelectorAll('br').forEach((br) => br.replaceWith('\n'))
  const lines = []
  const blockText = (element) =>
    Array.from(element.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6'))
      .flatMap((block) => block.textContent.split('\n'))
      .filter((line, i, all) => line !== '' || i < all.length - 1)
  for (const node of Array.from(root.childNodes)) {
    if (node.nodeType === 3) {
      if (node.textContent.trim()) lines.push(node.textContent.trim())
    } else if (node.nodeName === 'BLOCKQUOTE') {
      const quoted = blockText(node)
      ;(quoted.length ? quoted : node.textContent.split('\n')).forEach((line) =>
        lines.push(line ? `> ${line}` : '>')
      )
    } else if (node.nodeName === 'UL' || node.nodeName === 'OL') {
      // Items start with "- " or "1. " (nativeTextToHtml reads them back as a list).
      // Nested lists are flattened: each item appears once.
      const addList = (list) =>
        Array.from(list.children)
          .filter((li) => li.nodeName === 'LI')
          .forEach((li, i) => {
            const mark = list.nodeName === 'OL' ? `${i + 1}. ` : '- '
            const own = Array.from(li.childNodes).filter(
              (child) => !['UL', 'OL'].includes(child.nodeName)
            )
            const [first, ...rest] = own
              .map((child) => child.textContent)
              .join('\n')
              .split('\n')
            lines.push(mark + first, ...rest.map((line) => `  ${line}`))
            Array.from(li.children)
              .filter((child) => ['UL', 'OL'].includes(child.nodeName))
              .forEach(addList)
          })
      addList(node)
    } else {
      lines.push(...node.textContent.split('\n'))
    }
  }
  while (lines.length && lines[lines.length - 1] === '') lines.pop()
  return lines.join('\n')
}

const HTML_ESCAPES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}

// Text typed in a native field is plain text: keep its lines, never parse it
// as HTML or Markdown.
export function plainTextToHtml(text) {
  return String(text)
    .split(/\r\n|\r|\n/)
    .map(
      (line) =>
        `<p>${line.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char])}</p>`
    )
    .join('')
}

// Tells the app something it should know outside the ready/submit handshake,
// like a quote to show in its native composer. No-op outside the app.
export function postToNativeApp(message, win = window) {
  const bridge = win.ReactNativeWebView
  if (!bridge) return false
  post(bridge, message)
  return true
}

// Native composer text to editor HTML: like plainTextToHtml, but lines starting
// with "> " (how the app shows a quote) become a blockquote again.
export function nativeTextToHtml(text) {
  const blocks = []
  let quote = []
  let list = null
  const flushList = () => {
    if (list) {
      const tag = list.ordered ? 'ol' : 'ul'
      blocks.push(
        `<${tag}>${list.items
          .map((item) => `<li>${plainTextToHtml(item)}</li>`)
          .join('')}</${tag}>`
      )
    }
    list = null
  }
  const flushQuote = () => {
    if (quote.length)
      blocks.push(
        `<blockquote>${plainTextToHtml(quote.join('\n'))}</blockquote>`
      )
    quote = []
  }
  for (const line of String(text).split(/\r\n|\r|\n/)) {
    const item = line.match(/^(?:(-) |(\d+)\. )(.*)$/)
    if (line.startsWith('> ') || line === '>') {
      flushList()
      quote.push(line.replace(/^> ?/, ''))
    } else if (item) {
      flushQuote()
      const ordered = !item[1]
      if (list && list.ordered !== ordered) flushList()
      list ||= { ordered, items: [] }
      list.items.push(item[3])
    } else {
      flushQuote()
      flushList()
      blocks.push(plainTextToHtml(line))
    }
  }
  flushQuote()
  flushList()
  return blocks.join('')
}
